import type { AiProposal } from '@hearth/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Bot, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { ProposalComposer } from '@/components/customisation/ai-composer';
import { ProposalCard } from '@/components/customisation/ai-proposal-card';
import { QueryState } from '@/components/query-state';
import { Skeleton } from '@/components/ui/skeleton';
import { aiBuilderStatusQuery, aiProposalsQuery, pagesQuery } from '@/lib/customisation/queries';
import { useStudioTenant } from '@/routes/_app/admin/customisation';

export const Route = createFileRoute('/_app/admin/customisation/ai')({ component: Page });

/**
 * The AI builder: describe a change, review the proposal, apply it to the draft. The list
 * below is the tenant's recent proposals; the newest one sits on top with its preview open.
 */
function Page() {
  const tenant = useStudioTenant();
  const queryClient = useQueryClient();
  const status = useQuery(aiBuilderStatusQuery(tenant));
  const pages = useQuery(pagesQuery(tenant));
  const proposals = useQuery(aiProposalsQuery(tenant));
  const [latestId, setLatestId] = useState<string | null>(null);

  const onCreated = (p: AiProposal) => {
    setLatestId(p.id);
    queryClient.setQueryData(aiProposalsQuery(tenant).queryKey, (old) =>
      old ? { proposals: [p, ...old.proposals.filter((o) => o.id !== p.id)] } : old,
    );
    void queryClient.invalidateQueries({ queryKey: ['studio', tenant, 'ai'] });
  };
  const pageTitle = (p: AiProposal) =>
    p.pageId ? (pages.data?.pages.find((pg) => pg.id === p.pageId)?.title ?? null) : null;

  return (
    <div className="mx-auto grid max-w-[1100px] gap-8 px-4 py-8 sm:px-6 lg:px-8">
      <ProposalComposer
        tenant={tenant}
        pages={pages.data?.pages ?? []}
        status={status.data}
        onCreated={onCreated}
      />

      <QueryState
        query={proposals}
        skeleton={
          <div className="grid gap-3">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        }
      >
        {({ proposals: list }) => {
          const featured =
            list.find((p) => p.id === latestId) ?? list.find((p) => p.status === 'pending') ?? null;
          const rest = list.filter((p) => p.id !== featured?.id);
          return (
            <div className="grid gap-6">
              {featured ? (
                <section aria-label="Latest proposal" className="grid gap-2">
                  <h2 className="text-sm font-semibold tracking-wide uppercase">
                    {featured.status === 'pending' ? 'Ready for review' : 'Latest proposal'}
                  </h2>
                  <ProposalCard
                    proposal={featured}
                    tenant={tenant}
                    featured
                    pageTitle={pageTitle(featured)}
                  />
                </section>
              ) : null}
              {rest.length ? (
                <section aria-label="Past proposals" className="grid gap-2">
                  <h2 className="text-sm font-semibold tracking-wide uppercase">Past proposals</h2>
                  <div className="grid gap-3">
                    {rest.map((p) => (
                      <ProposalCard
                        key={p.id}
                        proposal={p}
                        tenant={tenant}
                        pageTitle={pageTitle(p)}
                      />
                    ))}
                  </div>
                </section>
              ) : null}
              {!list.length ? (
                <div className="text-muted-foreground flex items-center gap-3 rounded-xl border border-dashed px-4 py-8 text-sm">
                  {status.isPending ? (
                    <Loader2 className="size-5 animate-spin" aria-hidden />
                  ) : (
                    <Bot className="size-5" aria-hidden />
                  )}
                  No proposals yet. Pick an example above or describe a change to get the first one.
                </div>
              ) : null}
            </div>
          );
        }}
      </QueryState>
    </div>
  );
}
